import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The page header's action slot.
 *
 * "+ New task", "+ Share a headline", "+ Report an issue" used to be full-width
 * buttons at the top of each view's own markup. They belong in the header row,
 * beside the page title and Refresh — but the header lives in App while the
 * state that decides what each button does (which form is open, what the draft
 * is) lives inside each view. Lifting all of that into App would be a large
 * change for a visual rearrangement.
 *
 * So the header exposes an empty element, App hands it down through context, and
 * a view wraps its button in <HeaderAction>: it is still rendered by — and wired
 * to the state of — the view, but lands in the header's DOM. React keeps events
 * and context flowing through the portal as if it had never moved.
 *
 * Because the view only renders the button in its list mode (the form branches
 * return early), the header action disappears while a form is open, exactly as
 * the old in-page button did.
 */
const HeaderSlotContext = createContext<HTMLElement | null>(null)

export const HeaderSlotProvider = HeaderSlotContext.Provider

export default function HeaderAction({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderSlotContext)
  // Null for the one render before App's ref callback has run, and anywhere the
  // component is used outside the provider — in both cases, show nothing rather
  // than dropping the button into the page body.
  return slot ? createPortal(children, slot) : null
}
